import type {
  ChtLimits,
  ChtState,
  CylinderCount,
  CylinderReading,
  EngineReadings,
  MagCheckReading,
  PeakState,
} from '../types/engine';

/** When current EGT drops this many °F below the observed peak, we declare
 *  that cylinder "peaked". Absorbs normal instrument noise of a few degrees. */
const PEAK_DROP_THRESHOLD_F = 8;

/** Fresh readings struct for a given cylinder count. Called on engine-count
 *  changes and on explicit clears. */
export function makeEmptyReadings(count: CylinderCount): EngineReadings {
  return {
    cylinders: Array.from({ length: count }, (_, i) => ({ id: i + 1 })),
    magCheck: Array.from({ length: count }, (_, i) => ({ id: i + 1 })),
    lastUpdated: Date.now(),
  };
}

/** Apply a new EGT sample to a single cylinder. Updates peakEgt / peakedAt
 *  according to the simple rule: peak is the highest EGT we've seen, and the
 *  cylinder is declared peaked once EGT falls meaningfully below that peak. */
export function applyCylinderEgt(
  readings: EngineReadings,
  id: number,
  egt: number | undefined,
): EngineReadings {
  const now = Date.now();
  return {
    ...readings,
    cylinders: readings.cylinders.map((c) => {
      if (c.id !== id) return c;
      if (egt === undefined) {
        // Clearing the EGT leaves peak history untouched — the pilot may
        // just be mid-edit.
        return { ...c, egt: undefined };
      }
      const peakEgt = c.peakEgt === undefined ? egt : Math.max(c.peakEgt, egt);
      const peaked =
        c.peakedAt !== undefined
          ? c.peakedAt
          : peakEgt !== undefined && peakEgt - egt >= PEAK_DROP_THRESHOLD_F
            ? now
            : undefined;
      return { ...c, egt, peakEgt, peakedAt: peaked };
    }),
    lastUpdated: now,
  };
}

export function applyCylinderCht(
  readings: EngineReadings,
  id: number,
  cht: number | undefined,
): EngineReadings {
  return {
    ...readings,
    cylinders: readings.cylinders.map((c) =>
      c.id === id ? { ...c, cht } : c,
    ),
    lastUpdated: Date.now(),
  };
}

export function applyMagRise(
  readings: EngineReadings,
  id: number,
  side: 'left' | 'right',
  rise: number | undefined,
): EngineReadings {
  return {
    ...readings,
    magCheck: readings.magCheck.map((m) =>
      m.id === id ? { ...m, [side === 'left' ? 'leftRise' : 'rightRise']: rise } : m,
    ),
    lastUpdated: Date.now(),
  };
}

export function resetPeaks(readings: EngineReadings): EngineReadings {
  return {
    ...readings,
    cylinders: readings.cylinders.map((c) => ({
      id: c.id,
      egt: c.egt,
      cht: c.cht,
    })),
    lastUpdated: Date.now(),
  };
}

export function resetMagCheck(readings: EngineReadings): EngineReadings {
  return {
    ...readings,
    magCheck: readings.magCheck.map((m) => ({ id: m.id })),
    lastUpdated: Date.now(),
  };
}

/** Highest CHT across all entered cylinders, or undefined if none entered. */
export function maxCht(cylinders: CylinderReading[]): { id: number; cht: number } | undefined {
  let best: { id: number; cht: number } | undefined;
  for (const c of cylinders) {
    if (c.cht === undefined) continue;
    if (!best || c.cht > best.cht) best = { id: c.id, cht: c.cht };
  }
  return best;
}

/** Map a CHT value to a state band using the pilot's personal limits. */
export function classifyCht(cht: number | undefined, limits: ChtLimits): ChtState {
  if (cht === undefined) return 'unknown';
  if (cht >= limits.warn) return 'danger';
  if (cht >= limits.ok) return 'warn';
  return 'ok';
}

/** How a single cylinder relates to its observed peak, given the most recent
 *  EGT sample. Used by screens to colour the bar and show labels like
 *  "50 °F LOP". */
export function classifyPeakState(reading: CylinderReading): PeakState {
  if (reading.egt === undefined) return 'unknown';
  if (reading.peakEgt === undefined) return 'climbing';
  if (reading.peakedAt !== undefined) return 'peaked-lop';
  const delta = reading.peakEgt - reading.egt;
  if (delta < 3) return 'at-peak';
  return 'below-peak-rop';
}

/** The cylinder most likely to be the "reference" (last to peak) — the one
 *  with the highest peak EGT observed so far. Returns undefined if no peaks
 *  have been captured yet. */
export function referenceCylinder(cylinders: CylinderReading[]): CylinderReading | undefined {
  let best: CylinderReading | undefined;
  for (const c of cylinders) {
    if (c.peakEgt === undefined) continue;
    if (!best || (c.peakEgt ?? 0) > (best.peakEgt ?? 0)) best = c;
  }
  return best;
}

/** °F offset from a cylinder's observed peak. Positive = LOP, negative = ROP. */
export function offsetFromPeak(c: CylinderReading): number | undefined {
  if (c.egt === undefined || c.peakEgt === undefined) return undefined;
  return c.egt - c.peakEgt; // EGT below peak = negative = ROP; above doesn't
  // really happen after peak, the peakEgt would just climb with it.
}

/** Does the pilot have enough peak data to claim a meaningful leaning state? */
export function allCylindersPeaked(
  cylinders: CylinderReading[],
  count: CylinderCount,
): boolean {
  let peaked = 0;
  for (const c of cylinders) if (c.peakedAt !== undefined) peaked += 1;
  return peaked >= count;
}

/** Whether every cylinder has at least one EGT sample. */
export function allCylindersHaveEgt(
  cylinders: CylinderReading[],
  count: CylinderCount,
): boolean {
  let entered = 0;
  for (const c of cylinders) if (c.egt !== undefined) entered += 1;
  return entered >= count;
}

/** Helper for MagCheckReading: compute left-right delta if both sides entered. */
export function magDelta(m: MagCheckReading): number | undefined {
  if (m.leftRise === undefined || m.rightRise === undefined) return undefined;
  return m.leftRise - m.rightRise;
}

/** Set of cylinders where the entered mag-check EGT rise looks abnormal.
 *  Normal range is 50–150 °F per APS literature. Anything outside that band
 *  is flagged. */
export function flaggedMagCheckEntries(
  magCheck: MagCheckReading[],
  min = 50,
  max = 150,
): MagCheckReading[] {
  return magCheck.filter(
    (m) =>
      (m.leftRise !== undefined && (m.leftRise < min || m.leftRise > max)) ||
      (m.rightRise !== undefined && (m.rightRise < min || m.rightRise > max)),
  );
}
